import {
  Table as MUITable,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TablePaginationProps,
  TableRow,
  TableRowProps
} from '@material-ui/core'
import cx from 'classnames'
import React from 'react'
import { useTranslation } from 'react-i18next'
import {
  ROWS_PER_PAGE_OPTIONS,
  ROWS_PER_PAGE_OPTIONS_EXPANDED
} from '../table-controls/table-controls'
import { SpacingProps, useSpacing } from 'src/hooks/spacing'
import * as styles from './table.module.less'
import TablePaginationActions from '../table-pagination-actions/table-pagination-actions'

interface TableProps extends SpacingProps {
  fixedWidth?: boolean
}

export const Table: React.FC<TableProps> = ({ children, spacing, fixedWidth }) => {
  const spacingClass = useSpacing(spacing)
  return (
    <MUITable className={cx(fixedWidth ? styles.tableFixed : styles.table, spacingClass)}>
      {children}
    </MUITable>
  )
}

export const THead: React.FC = ({ children }) => {
  return <TableHead>{children}</TableHead>
}

export const TBody: React.FC = ({ children }) => {
  return <TableBody>{children}</TableBody>
}

interface WidthProps {
  widthClassName?: string
}

interface THProps extends WidthProps {
  noDivider?: boolean
}

export const TH: React.FC<THProps> = ({ children, widthClassName, noDivider }) => {
  const borderClass = useBorderClass(!noDivider)
  return (
    <TableCell className={cx(styles.header, widthClassName, borderClass)}>{children}</TableCell>
  )
}

interface TDProps extends WidthProps {
  noPad?: boolean
  noDivider?: boolean
}

export const TD: React.FC<TDProps> = ({ children, widthClassName, noPad, noDivider }) => {
  const borderClass = useBorderClass(!noDivider)
  return (
    <TableCell className={cx(noPad ? styles.dataNoPad : styles.data, borderClass, widthClassName)}>
      {children}
    </TableCell>
  )
}

export interface TRProps {
  onClick?: TableRowProps['onClick']
  disabled?: boolean
  scheduleRowStyle?: boolean
}

export const TR: React.FC<TRProps> = ({ children, disabled, onClick, scheduleRowStyle }) => {
  return (
    <TableRow
      onClick={disabled ? undefined : onClick}
      className={cx(onClick && !disabled ? styles.clickableRow : styles.row, {
        [styles.rowDisabled]: disabled,
        [styles.scheduleRowStyle]: scheduleRowStyle
      })}
    >
      {children}
    </TableRow>
  )
}

interface PaginationProps
  extends Pick<
    TablePaginationProps,
    'count' | 'rowsPerPage' | 'page' | 'onChangePage' | 'onChangeRowsPerPage'
  > {
  disableRowsPerPage?: boolean
  rowsPerPageOptions?: number[]
  rowsPerPageExpanded?: boolean
  customPlayerPagination?: string | number
}

export const TPagination: React.FC<PaginationProps> = props => {
  const { t } = useTranslation()
  const {
    disableRowsPerPage,
    rowsPerPageOptions,
    rowsPerPageExpanded,
    customPlayerPagination,
    ...otherProps
  } = props
  let rowsPerPage = rowsPerPageOptions ? rowsPerPageOptions : ROWS_PER_PAGE_OPTIONS
  rowsPerPage = rowsPerPageExpanded ? ROWS_PER_PAGE_OPTIONS_EXPANDED : rowsPerPage

  return (
    <TablePagination
        {...otherProps}
        component="div"
        labelDisplayedRows={({ count: total, ...rest }) =>
          t('showing displayed pages', { ...rest, total: customPlayerPagination ?? total })
        }
        rowsPerPageOptions={disableRowsPerPage ? [] : rowsPerPage}
        SelectProps={{ native: true }}
        ActionsComponent={TablePaginationActions}
        labelRowsPerPage={t('items')}
        classes={{
          toolbar: styles.paginationToolbar,
          spacer: styles.paginationSpacer,
          actions: styles.paginationActions,
          caption: styles.paginationCaption,
          selectRoot: styles.paginationSelect
        }}
      />
  )
}

interface PaginationInfoProps {
  className?: string
  from: number | string
  to: number | string
  total: number | string
  translationStr?: string
}

export const TPaginationInfoTop: React.FC<PaginationInfoProps> = ({
  className,
  from,
  to,
  total,
  translationStr = 'showing displayed pages'
}) => {
  const { t } = useTranslation()
  return (
    <p className={cx(className, styles.paginationInfo)}>
      {t(translationStr, {
        from,
        to,
        total
      })}
    </p>
  )
}

const useBorderClass = (border: boolean) => {
  return border ? styles.borderOn : styles.borderOff
}
